import React, { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import ScalableDashboard from './pages/ScalableDashboard';
import AlertPanel from './components/AlertPanel';
import AnalyticsView from './components/AnalyticsImproved';
import DeviceManagement from './components/DeviceManagement';
import SettingsPage from './pages/SettingsPage';
import DemoApp from './pages/DemoApp';
import webSocket from './services/websocket';
import { checkHealth } from './services/api';
import './styles/theme.css';

function App() {
  const [currentView, setCurrentView] = useState<'dashboard' | 'analytics' | 'devices' | 'alerts' | 'settings'>('dashboard');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [serverStatus, setServerStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [darkMode, setDarkMode] = useState(true); // Mode sombre par défaut
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);

  useEffect(() => {
    // Check URL for initial view
    const checkDemoMode = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const viewParam = urlParams.get('view');
      const hash = window.location.hash.replace('#', '');
      
      // Set initial view from URL and check if demo mode
      if (viewParam === 'demo' || hash === 'demo' || hash.startsWith('demo-') || window.location.pathname.includes('/demo')) {
        setIsDemoMode(true);
        // Ne pas définir currentView en mode démo
      } else {
        setIsDemoMode(false);
      }
    };

    checkDemoMode();

    // Écouter les changements de hash
    const handleHashChange = () => {
      checkDemoMode();
    };

    window.addEventListener('hashchange', handleHashChange);
    
    // Check server health
    const checkServerHealth = async () => {
      try {
        const response = await checkHealth();
        if (response.status === 200) {
          setServerStatus('online');
        }
      } catch (error) {
        setServerStatus('offline');
      }
    };

    checkServerHealth();
    const interval = setInterval(checkServerHealth, 30000); // Check every 30 seconds

    // Connect to WebSocket
    webSocket.connect();

    webSocket.on('connected', (connected: boolean) => {
      setIsConnected(connected);
    });

    return () => {
      clearInterval(interval);
      window.removeEventListener('hashchange', handleHashChange);
      webSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const handleViewChange = (view: string) => {
    if (view === 'dashboard' || view === 'analytics' || view === 'devices' || view === 'alerts' || view === 'settings') {
      setCurrentView(view as 'dashboard' | 'analytics' | 'devices' | 'alerts' | 'settings');
      // Update URL hash for direct access
      window.location.hash = view === 'dashboard' ? '' : view;
    }
  };

  const renderView = () => {
    switch (currentView) {
      case 'analytics':
        return <AnalyticsView selectedDeviceId={selectedDeviceId} onSelectDevice={setSelectedDeviceId} />;
      case 'devices':
        return <DeviceManagement onSelectDevice={setSelectedDeviceId} />;
      case 'alerts':
        return <AlertPanel />;
      case 'settings':
        return <SettingsPage />;
      default:
        return <ScalableDashboard />;
    }
  };

  // Si mode démo, rendre uniquement DemoApp (interface complète séparée)
  if (isDemoMode) {
    return (
      <>
        <Toaster position="top-right" />
        <DemoApp />
      </>
    );
  }

  // Interface de production normale
  return (
    <div className={`app-container ${darkMode ? 'dark' : ''}`}>
      <Toaster position="top-right" />
      
      {/* Overlay pour mobile */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'show' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      
      {/* Header unifié */}
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <button
              onClick={() => setSidebarOpen(true)}
              className="menu-button"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div>
              <h1 className="header-title">🌱 IoT Agricole</h1>
              <p className="header-subtitle">Surveillance en temps réel</p>
            </div>
          </div>
          
          <div className="header-right">
            {/* Indicateur de connexion */}
            <div className="connection-indicator">
              <div className={`status-dot ${isConnected ? 'status-online' : 'status-offline'}`}></div>
              <span className="hidden sm:inline">
                {isConnected ? 'Connecté' : 'Déconnecté'}
              </span>
            </div>
            
            {/* Toggle mode sombre */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="btn-icon"
              title={darkMode ? 'Mode clair' : 'Mode sombre'}
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
      </header>
      
      {/* Sidebar uniforme */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="card-title">🌱 IoT Agricole</h2>
              <p className="text-sm" style={{color: 'var(--text-secondary)'}}>
                Dashboard de surveillance
              </p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="btn-icon lg:hidden"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {[
            { id: 'dashboard', name: 'Tableau de Bord', icon: '🏠', desc: 'Vue d\'ensemble' },
            { id: 'devices', name: 'Capteurs', icon: '📡', desc: 'Gestion des dispositifs' },
            { id: 'analytics', name: 'Analyses', icon: '📊', desc: 'Tendances et statistiques' },
            { id: 'alerts', name: 'Alertes', icon: '🔔', desc: 'Notifications système' },
            { id: 'settings', name: 'Paramètres', icon: '⚙️', desc: 'Configuration' }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => {
                handleViewChange(item.id);
                setSidebarOpen(false);
              }}
              className={`nav-item ${currentView === item.id ? 'active' : ''}`}
            >
              <span className="text-lg">{item.icon}</span>
              <div>
                <div className="font-medium">{item.name}</div>
                <div className="text-sm" style={{color: 'var(--text-tertiary)', fontSize: '0.75rem'}}>
                  {item.desc}
                </div>
              </div>
            </button>
          ))}
        </nav>
        
        {/* Status système et lien démo en bas */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '1rem',
          borderTop: '1px solid var(--border-light)',
          backgroundColor: 'var(--bg-primary)'
        }}>
          {/* Bouton interface complète */}
          <a
            href="#demo"
            className="w-full block mb-3 p-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-lg transition-all text-center font-semibold shadow-lg"
          >
            🌾 Interface Complète
          </a>
          
          <div className="connection-indicator">
            <div className={`status-dot ${serverStatus === 'online' ? 'status-online' : 'status-offline'}`}></div>
            <div>
              <div className="text-sm font-medium">
                {serverStatus === 'online' ? 'Système Actif' : 'Système Hors Ligne'}
              </div>
              <div style={{fontSize: '0.75rem', color: 'var(--text-tertiary)'}}>
                {serverStatus === 'online' ? 'Connecté au broker MQTT' : 'Vérification...'}
              </div>
            </div>
          </div>
        </div>
      </aside>
      
      {/* Contenu principal */}
      <main className="main-content">
        {serverStatus === 'offline' ? (
          <div className="card" style={{
            backgroundColor: '#fef2f2',
            borderColor: '#fecaca',
            color: '#dc2626'
          }}>
            <div className="card-header">
              <h2 className="card-title" style={{color: '#dc2626'}}>
                🚫 Serveur Hors Ligne
              </h2>
            </div>
            <p>Impossible de se connecter au serveur backend. Veuillez vérifier que les services sont démarrés.</p>
          </div>
        ) : (
          renderView()
        )}
      </main>
      
      {/* Navigation mobile */}
      <nav className="mobile-nav">
        {[
          { id: 'dashboard', name: 'Tableau de Bord', icon: '🏠' },
          { id: 'devices', name: 'Capteurs', icon: '📡' },
          { id: 'analytics', name: 'Analyses', icon: '📊' },
          { id: 'alerts', name: 'Alertes', icon: '🔔' },
          { id: 'settings', name: 'Paramètres', icon: '⚙️' }
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => handleViewChange(item.id)}
            className={`mobile-nav-item ${currentView === item.id ? 'active' : ''}`}
          >
            <span style={{fontSize: '1.25rem'}}>{item.icon}</span>
            <span>{item.name}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default App;
